import { validations } from "../form"
import OnboardingContent from "../../components/onboarding/OnboardingContent"
import moment from "moment"
import * as Yup from "yup"
import useLocalStorage from "../useLocalStorage"
import { useUserProvider } from "../../providers/UserProvider"

const DEFAULT_VALUES = {
  isEdit: false,
  app_designed: "get_started",
  app_designed_due_date: moment().toISOString(),
  app_designed_notes: "",
  app_name: "",
  tagline: "",
  app_designed_fonts: "",
  app_designed_colors: "",
  app_designed_graphics: [],
  app_designed_assets_file: "",
  media_metadata: {},
  web_url: "",
}

const OnboardingAppDesigned = ({ handleSubmit, initialValues }) => {
  const [currentApp, setCurrentApp] = useLocalStorage('currentApp', null);
  const { user, setUser } = useUserProvider();

  const fields = [
    {
      component: "app_designed",
      name: "app_designed",
      label: "Status",
      placeholder: "",
      isFieldRow: true,
      required: false,
    },
    {
      component: "app_designed_web_url",
      name: "app_designed_web_url",
      label: "Web URL",
      placeholder: "",
      isFieldRow: true,
      required: false,
    },
    {
      component: "app_designed_due_date",
      name: "app_designed_due_date",
      label: "Due Date",
      placeholder: "",
      isFieldRow: true,
      required: false,
    },
    {
      component: "app_designed_notes",
      field: "textarea",
      name: "app_designed_notes",
      label: "Notes",
      placeholder: "",
      isFieldRow: true,
      rows: 10,
      required: false,
    },
    {
      component: "app_name",
      field: "text",
      name: "app_name",
      label: "App Name",
      placeholder: "",
      isFieldRow: true,
      required: false,
    },
    {
      component: "tagline",
      field: "text",
      label: "App Tagline",
      placeholder: "",
      required: false,
      isFieldRow: true,
    },
    {
      component: "app_designed_fonts",
      field: "textarea",
      name: "app_designed_fonts",
      label: "Fonts",
      placeholder: "",
      isFieldRow: true,
      rows: 10,
      required: false,
    },
    {
      component: "app_designed_colors",
      field: "textarea",
      name: "app_designed_colors",
      label: "Colors",
      placeholder: "",
      isFieldRow: true,
      rows: 10,
      required: false,
    },
    {
      component: "app_designed_graphics",
      name: "app_designed_graphics",
      label: "Graphics",
      placeholder: "",
      isFieldRow: true,
      required: false,
    },
    {
      component: "app_designed_assets_file",
      name: "app_designed_assets_file",
      label: "Assets",
      placeholder: "",
      isFieldRow: true,
      required: false,
    },
  ]

  const schema = {
    app_designed_notes: validations.long_text.max(65535, "Maximum 65535 Characters"),
    app_name: validations.str_notreq_30,
    tagline: validations.str_notreq_100,
    app_designed_fonts: validations.long_text.max(65535, "Maximum 65535 Characters"),
    app_designed_colors: validations.long_text.max(65535, "Maximum 65535 Characters"),
    app_designed_assets_file: validations.long_text.nullable(),
    app_designed_graphics: Yup.array().nullable(),
  };

  const handleFormatPayload = (values) => {
    const { isEdit, app_name, tagline, ...rest } = values

    const newPayload = {
      name: app_name,
      username: values.app_designed_web_url,
      setup: {
        header: {
          tagline: tagline,
        }
      },
      onboarding: {
        ...rest,
      }
    }

    return newPayload
  }

  // reformat data if components used vs payload data is different
  // if you didnt change payload response, skip this and just pass values to defaultValues props
  const formatDefaultValues = () => {
    if (initialValues?.app?.id) {
      const { app_designed_graphics: multipleGraphicsFile = [],  app_designed_graphics_file: singleGraphicsFile } = initialValues?.onboarding || {}

      let domain = initialValues?.app_data?.domains?.length > 0 ? initialValues?.app_data?.domains?.[0] : `${process.env.REACT_APP_WEB_FQDN}/`;
      domain = domain.endsWith("/") ? domain : `${domain}/`;
      const username = initialValues?.app_data?.username;
      const completeURL = `${domain}${username}`;

      const graphics = [...new Set([...(typeof singleGraphicsFile === "string" ? [singleGraphicsFile] : []), ...multipleGraphicsFile])]

      const newPayload = {
        ...DEFAULT_VALUES,
        app_designed_web_url: username,
        complete_url: completeURL,
        domain: initialValues?.app_data?.domains?.[0] || domain,
        app_designed: initialValues?.onboarding?.app_designed,
        app_designed_due_date: initialValues?.onboarding?.app_designed_due_date,
        app_designed_notes: initialValues?.onboarding?.app_designed_notes,
        app_name: initialValues?.app_data?.name,
        tagline: initialValues?.app_data?.setup?.header?.tagline,
        app_designed_fonts: initialValues?.onboarding?.app_designed_fonts,
        app_designed_colors: initialValues?.onboarding?.app_designed_colors,
        app_designed_graphics: graphics,
        app_designed_graphics_file: null,
        app_designed_assets_file: initialValues?.onboarding?.app_designed_assets_file,
        media_metadata: initialValues?.onboarding?.media_metadata,
      }

      return newPayload
    }

    return DEFAULT_VALUES
  }

  return {
    config: {
      label: "Onboarding - Assets",
      resourceEndpoint: "apps",
      columnsMetadata: [
        { customComponent: (
          <OnboardingContent
            title="Assets"
            defaultValues={formatDefaultValues()}
            schema={schema}
            fields={fields}
            handleSubmit={handleSubmit}
            handleFormatPayload={handleFormatPayload}
            successCallbackFn={(res) => {
              const selectedApp = {
                id: res.data.id,
                name: res.data.name,
                lite: res.data.setup.premium_lite,
                plus: res.data.setup.premium_plus,
                isPending: res.data.is_pending,
                initials: res.data.name
                  .split(" ")
                  .map((n) => n[0])
                  .join(""),
              }

              setUser({
                ...user,
                app: selectedApp,
                app_data: {
                  ...user.app_data,
                  name: res.data.name,
                  username: res.data.username,
                  setup: {
                    ...user.app_data.setup,
                    header: res.data.setup.header
                  }
                },
              });

              setCurrentApp(selectedApp)
            }}
          />
        ) },
      ],
    }
  }
}

export default OnboardingAppDesigned;